import React, { useEffect, useState } from 'react';
import Swal from 'sweetalert2';
import { Table, Button, Modal, Form, Input, Select, Space, Tag } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined, FolderOutlined } from '@ant-design/icons';
import BASEURL from '../API/Config';

const { Option } = Select;

const SubCategoryList = () => {
  const [subcategories, setSubcategories] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editSubcategory, setEditSubcategory] = useState(null);
  const [form] = Form.useForm();
  const [titles, setTitles] = useState(['']);
  const token = localStorage.getItem('token');

  const fetchSubcategories = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BASEURL}/s/getsub`);
      const data = await res.json();
      if (data.success && Array.isArray(data.data)) {
        setSubcategories(data.data.map(s => ({ ...s, title: s.title[0] })));
      }
    } catch { Swal.fire('Error', 'Failed to load subcategories.', 'error'); }
    finally { setLoading(false); }
  };

  const fetchCategories = async () => {
    try {
      const res = await fetch(`${BASEURL}/pcategory`);
      const data = await res.json();
      if (Array.isArray(data)) setCategories(data);
    } catch {}
  };

  useEffect(() => { fetchSubcategories(); fetchCategories(); }, []);

  const openModal = (sub = null) => {
    if (sub) {
      setEditSubcategory(sub);
      form.setFieldsValue({ parentCategory: sub.parentCategory?._id || '' });
      setTitles([sub.title]);
    } else {
      setEditSubcategory(null);
      form.resetFields();
      setTitles(['']);
    }
    setShowModal(true);
  };

  const handleSubmit = async (values) => {
    if (titles.some(t => !t.trim())) {
      Swal.fire('Warning', 'All title fields are required.', 'warning'); return;
    }
    try {
      const url = editSubcategory
        ? `${BASEURL}/pcategory/updatesub/${editSubcategory._id}`
        : `${BASEURL}/pcategory/createsub`;
      const res = await fetch(url, {
        method: editSubcategory ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ parentCategory: values.parentCategory, title: titles }),
      });
      if (!res.ok) throw new Error();
      Swal.fire('Success', editSubcategory ? 'Updated!' : 'Subcategory added!', 'success');
      setShowModal(false);
      fetchSubcategories();
    } catch { Swal.fire('Error', 'Failed to save.', 'error'); }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Delete Subcategory?', icon: 'warning', showCancelButton: true,
      confirmButtonColor: '#e53935', confirmButtonText: 'Yes, Delete',
    });
    if (!result.isConfirmed) return;
    const res = await fetch(`${BASEURL}/pcategory/deletesub/${id}`, {
      method: 'DELETE', headers: { Authorization: `Bearer ${token}` },
    });
    if (res.ok) { Swal.fire('Deleted!', '', 'success'); fetchSubcategories(); }
  };

  const filtered = subcategories.filter(s => s.title.toLowerCase().includes(search.toLowerCase()));

  const columns = [
    { title: '#', key: 'idx', width: 60, render: (_, __, i) => <span style={{ color: '#6b7280' }}>{i + 1}</span> },
    {
      title: 'SUBCATEGORY', key: 'title',
      render: (r) => (
        <Space>
          <div style={{ width: 34, height: 34, borderRadius: 8, background: '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#059669' }}>
            <FolderOutlined />
          </div>
          <span style={{ fontWeight: 600, color: '#212121' }}>{r.title}</span>
        </Space>
      ),
    },
    {
      title: 'CATEGORY', key: 'category',
      render: (r) => (
        <Tag color="blue" style={{ borderRadius: 50, fontWeight: 600 }}>
          {r.parentCategory?.title || 'N/A'}
        </Tag>
      ),
    },
    {
      title: 'ACTIONS', key: 'actions', width: 110,
      render: (r) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => openModal(r)}
            style={{ color: '#2874f0', borderColor: '#2874f0', borderRadius: 4 }} />
          <Button size="small" danger icon={<DeleteOutlined />} onClick={() => handleDelete(r._id)}
            style={{ borderRadius: 4 }} />
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 1 }}>Catalog</div>
          <h2 style={{ margin: 0, fontFamily: 'Outfit,sans-serif', fontSize: 22, fontWeight: 700, color: '#212121' }}>Subcategory List</h2>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => openModal()}
          style={{ background: '#2874f0', borderColor: '#2874f0', borderRadius: 4, height: 38, fontWeight: 600 }}>
          Add Subcategory
        </Button>
      </div>

      <div style={{ background: '#fff', borderRadius: 8, border: '1px solid #e8ecf0', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
        <div style={{ padding: '14px 20px', borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <Input prefix={<SearchOutlined style={{ color: '#9ca3af' }} />} placeholder="Search subcategory..."
            value={search} onChange={(e) => setSearch(e.target.value)} style={{ width: 260, borderRadius: 4 }} />
          <div style={{ marginLeft: 'auto', fontSize: 13, color: '#6b7280' }}>
            Total: <strong style={{ color: '#212121' }}>{filtered.length}</strong>
          </div>
        </div>
        <Table columns={columns} dataSource={filtered} rowKey="_id" loading={loading}
          pagination={{ pageSize: 10, showTotal: (t) => `${t} subcategories` }} size="middle" />
      </div>

      <Modal
        title={<span style={{ fontFamily: 'Outfit,sans-serif', fontWeight: 700 }}>{editSubcategory ? 'Edit Subcategory' : 'Add Subcategory'}</span>}
        open={showModal} onCancel={() => setShowModal(false)} footer={null} centered width={520}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit} style={{ marginTop: 16 }}>
          <Form.Item name="parentCategory" label="Parent Category" rules={[{ required: true, message: 'Select category' }]}>
            <Select placeholder="Select category" size="large" style={{ borderRadius: 4 }}>
              {categories.map(cat => <Option key={cat._id} value={cat._id}>{cat.title}</Option>)}
            </Select>
          </Form.Item>

          <div style={{ marginBottom: 8 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 10 }}>Subcategory Titles</div>
            {titles.map((t, idx) => (
              <div key={idx} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                <Input value={t} placeholder={`Title ${idx + 1}`} size="large"
                  onChange={(e) => { const arr = [...titles]; arr[idx] = e.target.value; setTitles(arr); }}
                  style={{ borderRadius: 4 }} />
                {titles.length > 1 && (
                  <Button danger onClick={() => setTitles(titles.filter((_, i) => i !== idx))}
                    style={{ borderRadius: 4 }}>✕</Button>
                )}
              </div>
            ))}
            <button type="button" onClick={() => setTitles([...titles, ''])}
              style={{ background: '#e8f1ff', border: '1px solid #2874f0', borderRadius: 4, padding: '6px 14px', color: '#2874f0', cursor: 'pointer', fontSize: 13, fontWeight: 500, marginTop: 4 }}>
              + Add Another Title
            </button>
          </div>

          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 16 }}>
            <Button onClick={() => setShowModal(false)}>Cancel</Button>
            <Button type="primary" htmlType="submit"
              style={{ background: '#2874f0', borderColor: '#2874f0', borderRadius: 4, fontWeight: 600 }}>
              {editSubcategory ? 'Update' : 'Add'}
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default SubCategoryList;